"use client";

import Link from "@/components/LocalizedLink";
import { useState, useRef, useEffect, memo } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { CONTACT_EMAIL } from "@/lib/constants";
import { useTranslation } from "@/lib/i18n";

/* ── FAQ entry shape (content built per-render with t()) ── */
type FaqEntry = { id: string; question: string; answer: string };

/* ── Chevron Icon ── */
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <motion.svg
      animate={{ rotate: open ? 180 : 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="w-5 h-5 shrink-0 text-accent"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </motion.svg>
  );
}

/* ── Single FAQ Item ── */
function FaqItem({
  faq,
  index,
  isOpen,
  onToggle,
}: {
  faq: FaqEntry;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <motion.div
      ref={ref}
      role="listitem"
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.35), ease: "easeOut" }}
      className={`rounded-[2px] border transition-colors duration-300 overflow-hidden ${
        isOpen
          ? "border-primary/40 bg-surface-light"
          : "border-border bg-surface hover:border-border/60 hover:bg-surface-light/50"
      }`}
    >
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
      >
        <span
          className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
            isOpen ? "text-foreground" : "text-foreground/85"
          }`}
        >
          {faq.question}
        </span>
        <ChevronIcon open={isOpen} />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-6 pb-6">
              <div className="h-px bg-border mb-4" />
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed break-words hyphens-auto">
                {faq.answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main Export ── */
export const FaqSection = memo(function FaqSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const FAQS: FaqEntry[] = [
    { id: "ip-transit", question: t("faq_q_ip_transit"), answer: t("faq_a_ip_transit") },
    { id: "latency-frankfurt", question: t("faq_q_latency_frankfurt"), answer: t("faq_a_latency_frankfurt") },
    { id: "latency-istanbul", question: t("faq_q_latency_istanbul"), answer: t("faq_a_latency_istanbul") },
    { id: "latency-london", question: t("faq_q_latency_london"), answer: t("faq_a_latency_london") },
    { id: "dark-fiber", question: t("faq_q_dark_fiber"), answer: t("faq_a_dark_fiber") },
    { id: "dwdm", question: t("faq_q_dwdm"), answer: t("faq_a_dwdm") },
    { id: "pops", question: t("faq_q_pops"), answer: t("faq_a_pops") },
    { id: "naas", question: t("faq_q_naas"), answer: t("faq_a_naas") },
    { id: "sla", question: t("faq_q_sla"), answer: t("faq_a_sla") },
    { id: "quote", question: t("faq_q_quote"), answer: t("faq_a_quote") },
    { id: "asn", question: t("faq_q_asn"), answer: t("faq_a_asn") },
    { id: "ipv6", question: t("faq_q_ipv6"), answer: t("faq_a_ipv6") },
  ];

  const FAQ_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  // Re-align #faq anchor after hydration. The section sits below heavy hero
  // sections whose framer-motion children mount asynchronously, so the
  // browser's initial pre-hydration scroll can land off-target. Re-scroll on
  // mount and on hashchange so deep links such as /#faq land correctly.
  useEffect(() => {
    const scrollToFaq = () => {
      if (typeof window === "undefined") return;
      if (window.location.hash !== "#faq") return;
      const el = document.getElementById("faq");
      if (!el) return;
      // Defer one frame so layout has settled.
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "auto", block: "start" });
      });
    };
    scrollToFaq();
    window.addEventListener("hashchange", scrollToFaq);
    return () => window.removeEventListener("hashchange", scrollToFaq);
  }, []);

  return (
    <section
      id="faq"
      className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-x-clip scroll-mt-24"
    >
      {/* Structured data for GEO / AI citation */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }}
      />

      {/* Ambient background */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/4 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/3 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-4xl">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <span
            className="font-mono text-[11px] sm:text-[12px] lowercase text-eyebrow"
            style={{ letterSpacing: "0.08em" }}
          >
            {t('faq_eyebrow')}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-foreground"
            style={{ fontWeight: 600 }}
          >
            {t('faq_heading')}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            {t('faq_subtitle')}
          </p>
        </motion.div>

        {/* Accordion */}
        <div className="space-y-3" role="list" aria-label={t("faq_aria_list")}>
          {FAQS.map((faq, index) => (
            <FaqItem
              key={faq.id}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>

        {/* Contact nudge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-12 text-center glass rounded-[2px] px-8 py-8"
        >
          <p className="text-foreground font-semibold text-base mb-1">
            {t('still_have_questions')}
          </p>
          <p className="text-muted-foreground text-sm mb-5">
            {t('faq_team_responds')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[2px] border border-border text-foreground text-sm font-medium hover:bg-surface-light transition-colors"
            >
              {t('contact_sales')}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <a
              href={`mailto:${CONTACT_EMAIL.sales}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[2px] border border-border text-foreground text-sm font-medium hover:bg-surface-light transition-colors"
            >
              <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              {CONTACT_EMAIL.sales}
            </a>
            <a
              href="tel:+35924200300"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[2px] border border-border text-foreground text-sm font-medium hover:bg-surface-light transition-colors"
            >
              <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
              +359 2 420 0300
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
});
FaqSection.displayName = "FaqSection";
